import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  CircularProgress
} from '@mui/material';
import { useEffect, useState } from 'react';
import Progress from './Progress';
import { useLiquidInstanceContext } from '../../context/liquidInstanceContext';
import styles from '../../styles/styles';
import { formatUnits } from 'viem';
import { useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { useSwitchChain } from 'wagmi';
import { useChainId } from 'wagmi';
import { relendWrappedAbi } from '../../assets/wagmi/generated';
import { useAccount } from 'wagmi';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CloseIcon from '@mui/icons-material/Close';

function Unwrap({ setIsUnwrapped }: { setIsUnwrapped: (isUnwrapped: boolean) => void }) {
  const { address } = useAccount();
  const { liquidInstanceConfiguration, normalizedAmount, setNormalizedAmount } = useLiquidInstanceContext();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();

  const { data: hash, isPending, writeContract } = useWriteContract();
  const { isLoading: isUnwrapping, isSuccess: isUnwrapped } = useWaitForTransactionReceipt({
    hash
  });

  useEffect(() => {
    if (isUnwrapped) {
      setIsUnwrapped(true);
      setNormalizedAmount(BigInt(0));
    }
  }, [isUnwrapped, setIsUnwrapped, setNormalizedAmount]);

  const getUnwrap = async () => {
    if (chainId !== liquidInstanceConfiguration.L1.chainId) {
      switchChain({ chainId: liquidInstanceConfiguration.L1.chainId });
    }

    try {
      if (!address) {
        throw new Error('No address found');
      }
      /// if allowance isn't enough, approve the amount
      writeContract({
        address: liquidInstanceConfiguration.L2?.L1WrappedTokenAddress as `0x${string}`,
        abi: relendWrappedAbi,
        functionName: 'withdrawTo',
        args: [address, normalizedAmount]
      });
    } catch (error) {
      console.error('Approval failed:', error);
    }
  };
  return (
    <>
      {isUnwrapping ? (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <CircularProgress />
          <Typography>Transaction is being processed</Typography>
        </Box>
      ) : (
        <Button sx={styles.gradientButton} onClick={getUnwrap} disabled={isPending || isUnwrapped}>
          Unwrap
        </Button>
      )}
    </>
  );
}

export default function UnwrapDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { liquidInstanceConfiguration, normalizedAmount } = useLiquidInstanceContext();
  const [isUnwrapped, setIsUnwrapped] = useState(false);
  const displayAmount = normalizedAmount
    ? formatUnits(normalizedAmount, liquidInstanceConfiguration.L1.L1TokenDecimals)
    : '0';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { ...styles.dialog }
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Progress isApproved={isUnwrapped} steps={1} />
        <IconButton onClick={onClose}>
          <CloseIcon sx={{ color: 'rgba(255, 255, 255, 0.76)' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: '300px' }}>
        {!isUnwrapped && (
          <>
            <Typography>Unwrap your tokens</Typography>
            <Box
              sx={{
                ...styles.greyBackground,
                padding: 1,
                borderRadius: 3,
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: 1
              }}
            >
              <Typography sx={{ ...styles.greyText }}>You will burn</Typography>
              <Typography>
                {' '}
                {displayAmount} {liquidInstanceConfiguration.L2?.L2TokenName}
              </Typography>
            </Box>
            <Box
              sx={{
                ...styles.greyBackground,
                padding: 1,
                borderRadius: 3,
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: 1
              }}
            >
              <Typography sx={{ ...styles.greyText }}>You will receive</Typography>
              <Typography>
                {' '}
                {displayAmount} {liquidInstanceConfiguration.L1.L1Token}
              </Typography>
            </Box>
            <Unwrap setIsUnwrapped={setIsUnwrapped} />
          </>
        )}
        {isUnwrapped && (
          <>
            <Typography>Unwrapped</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <IconButton>
                <TaskAltIcon sx={{ color: 'rgba(4, 255, 218, 1)', fontSize: '4rem' }} />
              </IconButton>
              <Typography sx={{ color: 'rgba(4, 255, 218, 1)' }}>Assets successfully unwrapped</Typography>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
